/* Elave: buy-box Description / How to use / Ingredients as one row of tabs instead of stacked accordions.
   Groups the consecutive (non-standalone) accordion blocks in the product info column under a bar of
   equal-width tabs. All closed by default; tap a tab to open its panel under the bar, tap it again to close.
   Opening, closing and switching glide the panel area to its new height and fade the text, so nothing snaps.
   On phones (CSS, below 768px) the bar is hidden and the same blocks show as slim stacked rows instead: the
   theme's own accordion opens them, and this script only keeps one open at a time.
   The accordion blocks stay as they are in the template, so their content is still edited in the theme editor. */
(() => {
  const ITEM = '.hdt-product-info__accordion:not(.hdt-product-info__accordion--standalone)';
  const EASE = 'cubic-bezier(.25, .1, .25, 1)';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const phone = window.matchMedia('(max-width: 767px)');
  let uid = 0;

  const hasContent = (item) => {
    const rte = item.querySelector('.rte');
    return !!rte && (rte.textContent.trim() !== '' || !!rte.querySelector('img, video, iframe, table'));
  };

  const build = (run) => {
    const items = run.filter(hasContent);
    if (items.length < 2) return;
    const wrap = document.createElement('div');
    wrap.className = 'hdt-product-info__item hdt-product-info__item--box elave-info-tabs';
    wrap.style.setProperty('--elave-tabs', items.length);
    const bar = document.createElement('div');
    bar.className = 'elave-info-tabs__bar';
    const panels = document.createElement('div');
    panels.className = 'elave-info-tabs__panels';
    items[0].before(wrap);
    wrap.append(bar, panels);

    const pairs = items.map((item) => {
      const details = item.querySelector('details');
      const summary = item.querySelector('summary');
      const panel = summary.nextElementSibling;
      panel.id ||= `ElaveInfoTab-${++uid}`;
      const tab = document.createElement('button');
      tab.type = 'button';
      // Same text classes as the accordion headings, so the labels match the theme's type exactly
      tab.className = 'elave-info-tabs__tab hdt-s-text hdt-text-sm hdt-uppercase';
      tab.setAttribute('aria-expanded', 'false');
      tab.setAttribute('aria-controls', panel.id);
      const plus = document.createElement('span');
      plus.className = 'elave-info-tabs__plus';
      plus.setAttribute('aria-hidden', 'true');
      const label = document.createElement('span');
      label.className = 'elave-info-tabs__label';
      label.textContent = summary.textContent.trim();
      tab.append(label, plus);
      bar.append(tab);
      item.classList.add('elave-info-tabs__panel');
      panels.append(item);
      details.removeAttribute('open');
      summary.setAttribute('aria-expanded', 'false');
      // Phone rows: a plus on the right (turns into a minus when open) in place of the theme caret
      const rowPlus = plus.cloneNode();
      summary.append(rowPlus);
      return { tab, details, summary, panel };
    });

    // An accordion with nothing in it (e.g. an unfilled metafield) has no tab; keep it out of the way.
    run.filter((item) => !items.includes(item)).forEach((item) => {
      item.hidden = true;
      panels.append(item);
    });

    const sync = () => {
      pairs.forEach(({ tab, details, summary }) => {
        const open = details.hasAttribute('open');
        tab.setAttribute('aria-expanded', `${open}`);
        summary.setAttribute('aria-expanded', `${open}`);
      });
      wrap.classList.toggle('is-open', pairs.some(({ details }) => details.hasAttribute('open')));
    };

    const markTabs = (next) => {
      pairs.forEach((p) => p.tab.setAttribute('aria-expanded', `${p === next}`));
      wrap.classList.toggle('is-open', !!next);
    };
    const setOpen = (next) => {
      pairs.forEach((p) => p !== next && p.details.removeAttribute('open'));
      if (next) {
        // The theme's accordion (phone rows) leaves its fade-out styles on the text when it closes a row
        next.panel.style.opacity = '';
        next.panel.style.transform = '';
        next.details.setAttribute('open', '');
      }
      sync();
    };

    // Tabs react straight away; the panel area then eases between the old and new heights.
    let seq = 0;
    let running = [];
    const show = async (next) => {
      const token = ++seq;
      const current = pairs.find((p) => p.details.hasAttribute('open'));
      // Measured before interrupting anything still animating, so a quick second tap carries on from where it is
      const from = panels.getBoundingClientRect().height;
      const fromOpacity = current ? Number(getComputedStyle(current.panel).opacity) : 1;
      running.forEach((a) => a.cancel());
      running = [];
      markTabs(next);
      if (reducedMotion.matches) {
        panels.style.height = '';
        return setOpen(next);
      }
      panels.style.height = `${from}px`;

      if (current && current !== next) {
        // Closing: fade the text while the area collapses. Switching: fade the old text out first.
        const fade = current.panel.animate([{ opacity: fromOpacity }, { opacity: 0 }], { duration: next ? 140 : 220, easing: 'ease-out', fill: 'forwards' });
        running.push(fade);
        if (!next) running.push(panels.animate([{ height: `${from}px` }, { height: '0px' }], { duration: 300, easing: EASE, fill: 'forwards' }));
        try { await Promise.all(running.map((a) => a.finished)); } catch (e) { return; }
        if (token !== seq) return;
        setOpen(next);
        running.forEach((a) => a.cancel());
        running = [];
        if (!next) {
          panels.style.height = '';
          return;
        }
      } else {
        setOpen(next);
      }

      // Unpin before measuring (a pinned box never reports less than its pinned height); the animation below
      // starts in the same frame, so the natural height never shows
      panels.style.height = '';
      const to = panels.getBoundingClientRect().height;
      running = [
        panels.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 340, easing: EASE }),
        next.panel.animate(
          [{ opacity: current === next ? fromOpacity : 0, transform: current === next ? 'none' : 'translateY(.6rem)' }, { opacity: 1, transform: 'none' }],
          { duration: 300, delay: current === next ? 0 : 90, easing: EASE, fill: 'backwards' }
        ),
      ];
    };

    // Phone rows: the theme's accordion opens the tapped row; this folds the previously open one down to its
    // heading (own animation — two theme accordion animations at once leave the closing row stuck half-open)
    const foldRow = (p) => {
      const d = p.details;
      p.summary.setAttribute('aria-expanded', 'false');
      d.style.overflow = 'hidden';
      const fade = p.panel.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150, easing: 'ease-out', fill: 'forwards' });
      d.animate([{ height: `${d.offsetHeight}px` }, { height: `${p.summary.offsetHeight}px` }], { duration: 280, easing: EASE })
        .finished.then(() => {
          d.removeAttribute('open');
          d.style.overflow = '';
          fade.cancel();
        }, () => {});
    };

    pairs.forEach((pair) => {
      const { tab, details } = pair;
      tab.addEventListener('click', () => show(tab.getAttribute('aria-expanded') === 'true' ? null : pair));
      // The theme editor opens/closes a block's accordion when it is selected; keep one panel open at a time.
      details.addEventListener('toggle', () => {
        if (details.open) {
          pairs.forEach((p) => {
            if (p.details === details || !p.details.hasAttribute('open')) return;
            // On phones fold the other row away rather than snapping it shut
            if (phone.matches && !reducedMotion.matches) foldRow(p);
            else p.details.removeAttribute('open');
          });
        }
        details.style.height = null;
        details.style.overflow = null;
        sync();
      });
    });
  };

  const init = (root = document) => {
    root.querySelectorAll('.hdt-product__info-container').forEach((container) => {
      if (container.querySelector(':scope > .elave-info-tabs')) return;
      const groups = [];
      let run = [];
      [...container.children].forEach((el) => {
        if (el.matches(ITEM)) {
          run.push(el);
          return;
        }
        if (run.length) groups.push(run);
        run = [];
      });
      if (run.length) groups.push(run);
      groups.forEach(build);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
